import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, CheckCircle2, Clock } from 'lucide-react';
import { InteriorSlide, SlideHeader } from './SlideShell';
import type { SlideProps } from './SlideShell';
export function Slide15Timeline({
  index
}: SlideProps) {
  const timeline = ['Scope confirmation', 'Master concept and structure', 'Master production', 'Internal review', 'Local regulatory review', 'Campaign launch', 'Monthly optimization'];
  return <InteriorSlide data-id="element-322" data-mp="2t558auhiphy2">
      <SlideHeader eyebrow="Timeline & Approval Considerations" data-id="element-323" data-mp="2t6on0gnizkxq">
        Scope finalization should begin immediately
      </SlideHeader>

      <div className="flex-1 flex flex-col lg:flex-row gap-12 lg:gap-16 max-w-6xl mx-auto w-full" data-id="element-324" data-mp="2y2n73s1tmgsc">
        {/* Left: Considerations */}
        <div className="flex-1 space-y-6" data-id="element-325" data-mp="25gm0eg2dr76i">
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3,
          duration: 0.5
        }} className="bg-[var(--surface)] p-6 rounded-2xl border border-black/5" data-id="element-326" data-mp="25fj6dz9rgomd">
            <h3 className="text-lg font-bold text-[var(--fg)] mb-4 flex items-center gap-2" data-id="element-327" data-mp="qcof9b3qaws4">
              <AlertTriangle className="h-5 w-5 text-[var(--brand)]" data-id="element-328" data-mp="ldtptqb9a9z1" />
              Approval Considerations
            </h3>
            <ul className="space-y-4" data-id="element-329" data-mp="gh37sxfw54az">
              {['Use of pre-approved GSK assets where possible', 'Internal GSK MLR review', 'Local health authority review per market', 'Expected authority review time of approximately 2–3 weeks', 'Summer holiday period', 'MSA finalization and vendor onboarding'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-330" data-mp="1wub9eptr8i49" data-mpi={i}>
                  <div className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[var(--brand)] flex-shrink-0" data-id="element-331" data-mp="1wutgyilvairh" />
                  <span className="text-[var(--muted)] leading-snug" data-id="element-332" data-mp="2fwk3sl54t2ue">
                    {item}
                  </span>
                </li>)}
            </ul>
          </motion.div>
        </div>

        {/* Right: Gantt Timeline */}
        <div className="flex-[1.5] w-full" data-id="element-333" data-mp="2bwjn0tuk8fk6">
          <motion.h3 initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.4
        }} className="text-lg font-bold text-[var(--fg)] mb-6" data-id="element-334" data-mp="2bxb9nf66j2yb">
            Suggested Timeline
          </motion.h3>

          <div className="bg-white rounded-2xl shadow-sm border border-black/5 p-6 relative overflow-hidden" data-id="element-335" data-mp="2un1fqyfe7b5j">
            {/* Vertical grid lines */}
            <div className="absolute top-0 bottom-0 left-1/3 w-px bg-black/5" data-id="element-336" data-mp="1e7zwkvpdnsph" />
            <div className="absolute top-0 bottom-0 left-1/2 w-px bg-black/5" data-id="element-337" data-mp="1j9720o41sfsz" />
            <div className="absolute top-0 bottom-0 left-[66%] w-px bg-black/5" data-id="element-338" data-mp="1bo3imblt69em" />
            <div className="absolute top-0 bottom-0 left-[83%] w-px bg-black/5" data-id="element-339" data-mp="16q1xll4to00i" />

            <div className="space-y-4 relative z-10" data-id="element-340" data-mp="1kpkv7k00bsyk">
              {timeline.map((step, i) => {
              // Calculate width and margin based on index to simulate Gantt
              const width = i === 4 ? 'w-1/3' : i === 6 ? 'w-1/4' : 'w-1/4';
              const ml = i === 0 ? 'ml-0' : i === 1 ? 'ml-[10%]' : i === 2 ? 'ml-[25%]' : i === 3 ? 'ml-[35%]' : i === 4 ? 'ml-[45%]' : i === 5 ? 'ml-[75%]' : 'ml-[80%]';
              const color = i === 4 ? 'bg-[var(--brand-lighter)] text-[var(--brand)]' : i === 5 ? 'bg-[var(--brand)] text-white' : 'bg-[var(--surface)] text-[var(--fg)]';
              return <motion.div key={step} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.5 + i * 0.1,
                duration: 0.5
              }} className="flex items-center gap-4" data-id="element-341" data-mp="rvg0bevscm7d" data-mpi={i}>
                    <div className="w-1/3 text-sm font-semibold text-[var(--fg)] text-right pr-4" data-id="element-342" data-mp="1flabls0gi2pi">
                      {step}
                    </div>
                    <div className="flex-1" data-id="element-343" data-mp="3hcvvcei3xpze">
                      <div className={`h-8 rounded-md flex items-center px-3 text-xs font-bold shadow-sm ${width} ${ml} ${color}`} data-id="element-344" data-mp="3hdp0xc5by9fy">
                        {i === 4 && <Clock className="h-3 w-3 mr-1.5" data-id="element-345" data-mp="1tqj31laa60sr" />}
                        {i === 5 && <CheckCircle2 className="h-3 w-3 mr-1.5" data-id="element-346" data-mp="1poxsi6l7xylq" />}
                      </div>
                    </div>
                  </motion.div>;
            })}
            </div>
          </div>
        </div>
      </div>
    </InteriorSlide>;
}